/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useState } from 'react';
import { Badge, Tooltip } from '@mui/material';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog } from 'primereact/dialog';
import FlashOffIcon from '@mui/icons-material/FlashOff';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import {
  ArrowBackIos,
  AddCircleOutline,
  Tune,
  Visibility,
  Map,
  TextSnippet,
  EmojiObjects
} from '@mui/icons-material';
import { DataTable } from 'primereact/datatable';
import { useLocation, useNavigate } from 'react-router-dom';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { toast } from 'react-toastify';
import { LoadingRowsComponent } from '../../../components/commons/LoadingRows/LoadingRows';
import {
  ButtonIconStyled,
  SharedWithBadge,
  SharedWithItem,
  StatusProcess,
  StyledButtonName
} from './allLocationListStyles';
import { Location, LocationByUser } from '../../../types/location/location';
import { setAllLocations } from '../../../store/slices/locations/location';
import Strings from '../../../i18n';
import { RightSideBar } from '../../../components/layout/rightSideBar/rightSideBar';
import { AddLocationBar } from '../addLocationBar/addLocationBar';
import { Header } from '../../../components/header/header';
import { viewRightSideBarEnum } from '../../../constants/sidebarEnums';
import { LocationDetailsBar } from '../locationDetailsBar/locationDetailsBar';
import { ShareLocationBar } from '../shareLocationBar/shareLocationBar';
import { TransferLocationBar } from '../transferLocationBar/transferLocationBar';
import { MoveLocationBar } from '../moveLocationBar/moveLocationBar';
import { MultipleLocationsDetailsSideBar } from '../multipleLocationsDetails/multipleLocationsDetailsBar';
import {
  StyledHeaderContainer,
  StyledTitleHeader,
  styledCheckboxHeader,
  styledColumnHeaderCheckbox
} from '../../../components/layout/main/mainStyles';
import { selectSystemLocationsReadWrite } from '../../../store/slices/systemPermissions/systemPermissions';
import { AllLocationsFilters } from './allLocationsFilters/allLocationsFilters';
import { ShareLocationsWithTargets } from '../shareLocationsWithTargets/shareLocationsWithTargets';
import { ChangeLatLongLocationBar } from '../changeLatLongLocation/changeLatLongLocation';
import { ContainerTable } from '../locationsList/locationListStyles';
import {
  useGetLocationsMutation,
  useGetLocationTypesMutation,
  useTurnOffLocationMutation,
  useTurnOnLocationMutation
} from '../../../services/locations/locationsService';
import { ChangeLocationType } from '../changeLocationType/changeLocationType';
import { DeferOnOffMethod } from '../../device/DeferOnOffMethod/deferOnOffMethod';
import { AddNoteSideBar } from '../../../components/commons/notes/addNoteSideBar/addNoteSideBar';
import { EnhancedPaginator, OrderByEnum } from './allLocationsPaginator';
import { StyledAddDeviceToLocationButton } from '../../device/devicesTemplate/devicesTemplateStyle';
import { AddLocationToBrand } from '../addLocationToBrand/addLocationToBrand';

export const AllLocationsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { search } = location;
  const queryParams = new URLSearchParams(search);
  const filtersCount = Array.from(queryParams.keys()).length;
  const totalItems = 250;

  const pageParam = queryParams.get('page');
  const filterByLabel = queryParams.get('filterByLabel');
  const filterByOwner = queryParams.get('filterByOwner');
  const filterSharedWith = queryParams.get('filterSharedWith');
  const orderParam = queryParams.get('order');
  const orderByParam = queryParams.get('orderBy');

  const [paginator, setPaginator] = useState({
    currentPage: pageParam ? +pageParam : 1,
    pageSize: totalItems
  });

  const [orderState, setOrderState] = useState({
    order: orderParam || 'asc',
    orderBy: orderByParam !== null ? +orderByParam : OrderByEnum.NAME
  });

  const [allLocationsList, setAllLocationsList] = useState<Location[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [openSharedWithDialog, setOpenSharedWithDialog] = useState(false);
  const [selectToShowSharedWith, setSelectToShowSharedWith] =
    useState<Location>();
  const [openSideBar, setOpenSideBar] = useState(false);
  const permissionLocationReadWrite = useSelector(
    selectSystemLocationsReadWrite
  );

  const [selectedLocations, setSelectedLocation] = useState<Location[]>([]);
  const [turnOffLocation, { isSuccess: isSuccessTurnOffLocation }] =
    useTurnOffLocationMutation();

  const [turnOnLocation, { isSuccess: isSuccessTurnOnLocation }] =
    useTurnOnLocationMutation();

  const [getLocations, { isLoading, isSuccess, data }] =
    useGetLocationsMutation();
  const [
    getLocationTypes,
    {
      isLoading: isLoadingLocationTypes,
      isSuccess: isSuccessLocationTypes,
      data: locationTypesData
    }
  ] = useGetLocationTypesMutation();
  const [sideBarView, setSideBarView] = useState(
    viewRightSideBarEnum.addLocation
  );

  const [locationToBrandData, setLocationToBrandData] = useState<
    Location | undefined
  >();

  const updateUrlParams = (newParams: any) => {
    const currentParams = new URLSearchParams(search);

    Object.entries(newParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        currentParams.set(key, value.toString());
      } else {
        currentParams.delete(key);
      }
    });

    navigate(`${location.pathname}?${currentParams.toString()}`, {
      replace: true
    });
  };

  const handleOrderChange = (newOrder: string) => {
    setOrderState((prev) => ({
      ...prev,
      order: newOrder
    }));
    updateUrlParams({ order: newOrder });
  };

  const handleOrderByChange = (newOrderBy: OrderByEnum) => {
    setOrderState((prev) => ({
      ...prev,
      orderBy: newOrderBy
    }));
    updateUrlParams({ orderBy: newOrderBy });
  };

  const onPageChange = (event: any) => {
    const newPage = event.page + 1;
    setPaginator({
      currentPage: newPage,
      pageSize: event.rows
    });
    updateUrlParams({ page: newPage });
  };

  const onSort = (event: any) => {
    const newOrder = event.sortOrder === 1 ? 'asc' : 'desc';
    let newOrderBy;

    switch (event.sortField) {
      case 'locationName':
        newOrderBy = OrderByEnum.NAME;
        break;
      case 'locationType':
        newOrderBy = OrderByEnum.TYPE;
        break;
      case 'ownerName':
        newOrderBy = OrderByEnum.OWNER;
        break;
      case 'locationAddress':
        newOrderBy = OrderByEnum.ADDRESS;
        break;
      case 'createdAt':
        newOrderBy = OrderByEnum.CREATED_AT;
        break;
      case 'deviceCount':
        newOrderBy = OrderByEnum.DEVICES;
        break;
      case 'sharedWith':
        newOrderBy = OrderByEnum.SHARES;
        break;
      default:
        newOrderBy = OrderByEnum.NAME;
    }

    setOrderState({
      order: newOrder,
      orderBy: newOrderBy
    });
    updateUrlParams({ order: newOrder, orderBy: newOrderBy });
  };

  const SharedWithButton = ({ sharedWith, ...rowData }: Location) => {
    if (sharedWith.length > 0) {
      return (
        <SharedWithBadge
          badgeContent={sharedWith.length}
          color="primary"
          data-testid="add-button"
          onClick={() => {
            setSelectToShowSharedWith({ sharedWith, ...rowData });
            setOpenSharedWithDialog(true);
          }}
        >
          <Visibility />
        </SharedWithBadge>
      );
    }
    return sharedWith.length;
  };

  const loadData = () => {
    const additionalParams = {
      filterByLabel,
      filterByOwner,
      filterSharedWith,
      order: orderState.order,
      orderBy: orderState.orderBy
    };

    const filteredParams = Object.fromEntries(
      Object.entries(additionalParams).filter(([_, v]) => v != null)
    );

    getLocations({
      page: paginator.currentPage,
      size: paginator.pageSize,
      additionalParams: filteredParams
    });

    if (!locationTypesData) {
      getLocationTypes();
    }
  };

  useEffect(() => {
    loadData();
  }, [
    paginator.currentPage,
    paginator.pageSize,
    orderState.order,
    orderState.orderBy,
    filterByLabel,
    filterByOwner,
    filterSharedWith
  ]);

  useEffect(() => {
    loadData();
  }, [
    paginator.currentPage,
    paginator.pageSize,
    orderState.order,
    orderState.orderBy
  ]);

  useEffect(() => {
    if (isSuccess && data) {
      dispatch(setAllLocations(data.data));
      const filteredLocations = data.data || data;

      if (Array.isArray(filteredLocations)) {
        setAllLocationsList(filteredLocations);
        setTotalRecords(data.totalItems || filteredLocations.length);
      }
    }
  }, [isSuccess]);

  const openLocationSideBarFor = (view: number) => {
    setOpenSideBar(true);
    setSideBarView(view);
  };

  const selectLocation = (params: any) => {
    navigate(`/locations/${params.locationId}/devices`);
  };

  const getStatus = (item: any) => {
    return <StatusProcess IsActive={item.enabled} marginRight={2} />;
  };

  const redirectToNotes = (item: Location) => {
    navigate('/my-notes', {
      state: {
        type: 'Locations',
        id: item.locationId
      }
    });
  };

  const getName = (item: Location) => {
    return (
      <>
        <StyledButtonName onClick={() => selectLocation(item)}>
          {item.locationName}
        </StyledButtonName>
        {item.hasNotes && (
          <ButtonIconStyled size="small" onClick={() => redirectToNotes(item)}>
            <TextSnippet fontSize="small" />
          </ButtonIconStyled>
        )}
      </>
    );
  };

  const reloadTablesAnCloseSidebar = (needsReload = true) => {
    if (needsReload) {
      loadData();
    }
    setOpenSideBar(false);
  };

  const switchSideBar = () => {
    const locations: LocationByUser[] = selectedLocations.map((element) => ({
      locationId: element.locationId,
      locationType: element.locationType,
      locationName: element.locationName,
      locationAddress: '',
      locationRemoteAccessToken: '',
      isOwner: false,
      ownerEmail: '',
      organizationId: 0,
      organizationName: '',
      locationRoleId: 0,
      hasNotes: element.hasNotes,
      hasDevices: Boolean(element.deviceCount),
      ownerId: element.ownerId
    }));
    switch (sideBarView) {
      case viewRightSideBarEnum.addLocation:
        return (
          <AddLocationBar
            reloadTable={() => {
              reloadTablesAnCloseSidebar(true);
            }}
            setOpenRightSideBar={setOpenSideBar}
          />
        );
      case viewRightSideBarEnum.locationDetails:
        return (
          <LocationDetailsBar
            openRightSideBarForMethod={openLocationSideBarFor}
            reloadTable={() => {
              reloadTablesAnCloseSidebar(true);
            }}
            locationOrg={locations[0] ?? []}
            setOpenRightSideBar={setOpenSideBar}
          />
        );
      case viewRightSideBarEnum.shareLocation:
        return (
          <ShareLocationBar
            reloadTable={() => reloadTablesAnCloseSidebar(true)}
            selectedLocations={locations.map((loc) => loc.locationId) ?? []}
            setOpenRightSideBar={setOpenSideBar}
          />
        );
      case viewRightSideBarEnum.shareLocationsWithTargets:
        return (
          <ShareLocationsWithTargets
            reloadTable={() => reloadTablesAnCloseSidebar(true)}
            selectedLocations={locations.map((loc) => loc.locationId) ?? []}
            setOpenRightSideBar={setOpenSideBar}
          />
        );
      case viewRightSideBarEnum.deferOnOffMethod:
        return (
          <DeferOnOffMethod
            locationId={locations[0]?.locationId ?? 0}
            reloadTable={async () => reloadTablesAnCloseSidebar(true)}
            setOpenRightSideBar={setOpenSideBar}
            clearSelectedLocations={() => setSelectedLocation([])}
          />
        );
      case viewRightSideBarEnum.changeLocationType:
        return (
          <ChangeLocationType
            reloadTable={() => reloadTablesAnCloseSidebar(true)}
            selectedLocation={{
              locationId: locations[0]?.locationId ?? 0,
              locationType: locations[0]?.locationType ?? 0,
              name: locations[0]?.locationName ?? ''
            }}
            setOpenRightSideBar={setOpenSideBar}
          />
        );
      case viewRightSideBarEnum.transferLocation:
        return (
          <TransferLocationBar
            reloadTable={() => reloadTablesAnCloseSidebar(true)}
            selectedLocations={locations.map((loc) => loc.locationId) ?? []}
            setOpenRightSideBar={setOpenSideBar}
          />
        );
      case viewRightSideBarEnum.moveLocation:
        return (
          <MoveLocationBar
            reloadTable={() => reloadTablesAnCloseSidebar(true)}
            idLocations={locations.map((loc) => loc.locationId) ?? []}
            setOpenRightSideBar={setOpenSideBar}
          />
        );
      case viewRightSideBarEnum.multipleLocationsDetails:
        return (
          <MultipleLocationsDetailsSideBar
            reloadTable={() => reloadTablesAnCloseSidebar(true)}
            openRightSideBarForMethod={openLocationSideBarFor}
            selectedLocations={locations.map((loc) => loc.locationId) ?? []}
            setOpenRightSideBar={setOpenSideBar}
            selectedLocationsData={locations}
          />
        );
      case viewRightSideBarEnum.allLocationsFilters:
        return (
          <AllLocationsFilters
            reloadTable={() => reloadTablesAnCloseSidebar(false)}
            setOpenRightSideBar={setOpenSideBar}
            openRightSideBar={openSideBar}
          />
        );
      case viewRightSideBarEnum.changeLatLongLocation:
        return (
          <ChangeLatLongLocationBar
            location={{
              locationId: selectedLocations[0].locationId,
              lat: selectedLocations[0].lat ?? 0,
              lon: selectedLocations[0].lon ?? 0
            }}
            setOpenRightSideBar={setOpenSideBar}
            reloadTable={() => reloadTablesAnCloseSidebar(true)}
          />
        );
      case viewRightSideBarEnum.addNote:
        return (
          <AddNoteSideBar
            closeSideBar={() => {
              reloadTablesAnCloseSidebar(true);
            }}
            targetId={selectedLocations[0].locationId}
            type="location"
          />
        );
      case viewRightSideBarEnum.addLocationToBrand:
        return locationToBrandData ? (
          <AddLocationToBrand
            reloadTable={loadData}
            setOpenRightSideBar={setOpenSideBar}
            location={locationToBrandData}
          />
        ) : null;
      default:
        return (
          <AddLocationBar
            reloadTable={() => loadData()}
            setOpenRightSideBar={setOpenSideBar}
          />
        );
    }
  };
  useEffect(() => {
    if (selectedLocations.length === 1) {
      openLocationSideBarFor(viewRightSideBarEnum.locationDetails);
    } else if (selectedLocations.length > 1) {
      openLocationSideBarFor(viewRightSideBarEnum.multipleLocationsDetails);
    } else {
      openLocationSideBarFor(viewRightSideBarEnum.empty);
      setOpenSideBar(false);
    }
  }, [selectedLocations]);

  const getHeaderCheckbox = () => {
    return (
      <Checkbox
        className="p-checkbox"
        style={{
          visibility: selectedLocations.length ? 'visible' : 'hidden',
          ...styledCheckboxHeader
        }}
        checked={
          (allLocationsList &&
            selectedLocations &&
            selectedLocations.length > 0) ??
          false
        }
        onChange={() => {
          setSelectedLocation([]);
        }}
      />
    );
  };

  const redirectToMap = (rowData: Location) => {
    if (rowData.lat && rowData.lon) {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${rowData.lat},${rowData.lon}`,
        '_blank'
      );
    } else {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${rowData.locationAddress}`,
        '_blank'
      );
    }
  };

  const getAddressAndRedirectToMap = (rowData: Location) => {
    return (
      <span>
        {rowData.locationAddress && (
          <ButtonIconStyled size="small" onClick={() => redirectToMap(rowData)}>
            <Map />
          </ButtonIconStyled>
        )}
        {rowData.locationAddress}
      </span>
    );
  };

  const getOwnerName = (rowData: Location) => {
    return (
      <Tooltip title={Strings.users.gotoUser}>
        <StyledButtonName
          onClick={() => navigate(`/users?filterByLabel=${rowData.ownerEmail}`)}
        >
          {rowData.ownerName ?? rowData.ownerEmail}
        </StyledButtonName>
      </Tooltip>
    );
  };

  const getDeviceStatus = (rowData: Location) => {
    return `${rowData.totalDeviceConnected ?? '-'} /
      ${rowData.deviceCount ?? '-'}
    `;
  };

  const getSortField = (orderByValue: OrderByEnum) => {
    switch (orderByValue) {
      case OrderByEnum.NAME:
        return 'locationName';
      case OrderByEnum.TYPE:
        return 'locationType';
      case OrderByEnum.OWNER:
        return 'ownerName';
      case OrderByEnum.ADDRESS:
        return 'locationAddress';
      case OrderByEnum.CREATED_AT:
        return 'createdAt';
      case OrderByEnum.DEVICES:
        return 'deviceCount';
      case OrderByEnum.SHARES:
        return 'sharedWith.length';
      default:
        return 'locationName';
    }
  };

  const getBrandAssign = (item: Location) => {
    return (
      <StyledAddDeviceToLocationButton
        onClick={() => {
          setLocationToBrandData(item);
          setOpenSideBar(true);
          setSideBarView(viewRightSideBarEnum.addLocationToBrand);
        }}
      >
        {item.brandName || ''}
      </StyledAddDeviceToLocationButton>
    );
  };

  useEffect(() => {
    if (isSuccessTurnOffLocation || isSuccessTurnOnLocation) {
      toast.success(Strings.locations.commandSuccessfully);
    }
  }, [isSuccessTurnOffLocation, isSuccessTurnOnLocation]);

  return (
    <>
      <Header>
        <StyledHeaderContainer>
          <div className="row justify-content-between">
            <div className="col-1 col-sm-4 text-left">
              <ButtonIconStyled size="small" onClick={() => navigate(-1)}>
                <ArrowBackIos />
              </ButtonIconStyled>
            </div>
            <div className="col-5 col-sm-4 text-center">
              <StyledTitleHeader align="center">
                {Strings.locations.allLocations}
              </StyledTitleHeader>
            </div>
            <div className="col-6 col-sm-4 text-right">
              <ButtonIconStyled
                size="small"
                onClick={() => {
                  setOpenSideBar(true);
                  setSideBarView(viewRightSideBarEnum.addLocation);
                }}
              >
                <AddCircleOutline />
              </ButtonIconStyled>
              <ButtonIconStyled
                size="small"
                onClick={() => {
                  setOpenSideBar(true);
                  setSideBarView(viewRightSideBarEnum.allLocationsFilters);
                }}
              >
                <Badge badgeContent={filtersCount} color="warning">
                  <Tune />
                </Badge>
              </ButtonIconStyled>
              <Tooltip title={Strings.serverAccess.devices.turnOffDevices}>
                <ButtonIconStyled
                  size="small"
                  data-testid="off-command"
                  onClick={() => {
                    turnOffLocation(
                      selectedLocations.map((loc) => loc.locationId)
                    );
                  }}
                >
                  <FlashOffIcon />
                </ButtonIconStyled>
              </Tooltip>
              <Tooltip title={Strings.serverAccess.devices.turnOn}>
                <ButtonIconStyled
                  data-testid="on-command"
                  size="small"
                  onClick={() => {
                    turnOnLocation(
                      selectedLocations.map((loc) => loc.locationId)
                    );
                  }}
                >
                  <FlashOnIcon />
                </ButtonIconStyled>
              </Tooltip>
            </div>
          </div>
        </StyledHeaderContainer>
      </Header>

      <ContainerTable component="main">
        {(isLoading || isLoadingLocationTypes) && <LoadingRowsComponent />}
        {isSuccess && isSuccessLocationTypes && locationTypesData && data && (
          <>
            <DataTable
              selection={selectedLocations}
              selectionMode="multiple"
              metaKeySelection
              onSelectionChange={(e) => setSelectedLocation(e.value)}
              className="mt-3"
              rowHover
              value={allLocationsList}
              size="small"
              scrollable
              scrollHeight="flex"
              selectionPageOnly
              sortField={getSortField(orderState.orderBy)}
              sortOrder={orderState.order === 'asc' ? 1 : -1}
              onSort={onSort}
              removableSort={false}
              tableStyle={{ padding: 0, margin: 0, fontSize: '14px' }}
            >
              <Column selectionMode="multiple" frozen />
              <Column
                field="enabled"
                header=""
                sortable
                body={getStatus}
                headerStyle={{ width: '1.5rem', textAlign: 'center' }}
              />
              <Column
                field="locationName"
                header="Name"
                sortable
                body={getName}
              />
              <Column
                field="locationType"
                header="Type"
                sortable
                body={(rowData) =>
                  locationTypesData.find(
                    (type) => type.id === rowData.locationType
                  )?.name
                }
              />
              <Column
                field="ownerName"
                header="Owner"
                sortable
                body={getOwnerName}
              />
              <Column
                field="locationAddress"
                header="Address"
                sortable
                body={getAddressAndRedirectToMap}
              />
              <Column
                field="createdAt"
                body={(rowData) =>
                  moment(rowData.createdAt).format('YY/MM/DD HH:mm')
                }
                header="Created At"
                sortable
                dataType="date"
              />
              <Column
                field="deviceCount"
                header="Devices"
                sortable
                body={getDeviceStatus}
              />
              <Column
                field="sharedWith"
                header="Shares"
                sortable
                body={SharedWithButton}
              />
              <Column field="createdBy" header="Created By" />
              <Column
                field="brandName"
                header="Brand"
                sortable
                body={getBrandAssign}
              />
            </DataTable>
            <EnhancedPaginator
              first={(paginator.currentPage - 1) * paginator.pageSize}
              rows={paginator.pageSize}
              totalRecords={totalRecords}
              rowsPerPageOptions={[50, 100, 250, 500]}
              onPageChange={onPageChange}
              orderBy={orderState.orderBy}
              order={orderState.order}
              handleOrderChange={handleOrderChange}
              handleOrderByChange={handleOrderByChange}
            />
          </>
        )}
      </ContainerTable>
      <Dialog
        header="Shared With"
        visible={openSharedWithDialog}
        onHide={() => {
          setOpenSharedWithDialog(false);
        }}
        style={{ width: '50vw' }}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
      >
        {selectToShowSharedWith &&
          selectToShowSharedWith.sharedWith.map((user) => (
            <SharedWithItem key={user.email}>{user.email}</SharedWithItem>
          ))}
      </Dialog>
      <RightSideBar
        open={openSideBar}
        onMenuClose={() => {
          setOpenSideBar(false);
        }}
      >
        {switchSideBar()}
      </RightSideBar>
    </>
  );
};
